import { ReactNode, useEffect, useRef } from 'react';
import { Box, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import useAiChat from '../../hooks/useAiChat';
import { AiChatMessage as AiChatMessageType } from './sseProtocol';
import AiMessage from './AiMessage';
import AiChatInput from './AiChatInput';

const SUGGESTIONS = [
  'Show my available containers',
  'How many reuses did we confirm last month?',
  'How much CO2 have we saved this year?',
];

// The hemisphere-style working trace: each backend status breadcrumb as a
// step in its own assistant-side bubble — done steps ticked, current step
// spinning. When the answer starts streaming, the trace shows "Writing…".
const AiWorkingBubble = ({ trace }: { trace: string[] }) => {
  const steps = (trace.length ? trace : ['Working']).map((step) =>
    step === 'Compiling response' ? 'Writing response…' : step
  );
  return (
    <Box display="flex" justifyContent="flex-start" my={1} data-testid="ai-status">
      <Box
        sx={{
          background: '#fff',
          border: '1px solid #e4e7eb',
          borderRadius: '12px 12px 12px 2px',
          px: 2,
          py: 1.25,
          minWidth: 220,
        }}
      >
        {steps.map((step, index) => {
          const isCurrent = index === steps.length - 1;
          return (
            <Box key={`${step}-${index}`} display="flex" alignItems="center" gap={1} py={0.25}>
              {isCurrent ? (
                <CircularProgress size={12} sx={{ color: '#0088CF' }} />
              ) : (
                <CheckIcon sx={{ fontSize: 13, color: '#16a34a' }} />
              )}
              <Typography
                variant="caption"
                sx={{ color: isCurrent ? 'text.primary' : 'text.secondary', fontSize: 12.5 }}
              >
                {step}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

type AiChatViewProps = {
  scope?: string | null; // "all" | owner id | null (own scope)
  conversationId: string;
  seedMessages?: AiChatMessageType[];
  disabled?: boolean;
  disabledReason?: string;
  // Optional toolbar content rendered in the chat card's header row
  // (e.g. the admin account-scope selector).
  headerContent?: ReactNode;
  headerCaption?: string;
  // Panel mode: no card chrome (the panel supplies its own frame/header).
  flat?: boolean;
  hideHeader?: boolean;
};

// One chat session, rendered as a self-contained card. Remount (via key) to
// clear or resume a different conversation — mirrors HemIQ's chatKey pattern.
const AiChatView = ({
  scope,
  conversationId,
  seedMessages,
  disabled,
  disabledReason,
  headerContent,
  headerCaption,
  flat,
  hideHeader,
}: AiChatViewProps) => {
  const {
    messages,
    loading,
    trace,
    partial,
    queuedMessages,
    sendMessage,
    stopGeneration,
    clearQueuedMessage,
  } = useAiChat({ scope, conversationId, initialMessages: seedMessages });
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, trace, partial]);

  return (
    <Paper
      variant={flat ? 'elevation' : 'outlined'}
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        borderRadius: flat ? 0 : 2,
        overflow: 'hidden',
      }}
    >
      {hideHeader ? null : (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        px={2}
        py={1.25}
        sx={{ borderBottom: '1px solid', borderColor: 'divider', background: '#fff' }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #0088CF, #2563eb)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" lineHeight={1.2}>
              StackIQ
            </Typography>
            <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
              {headerCaption || 'Chatting with your container data'}
            </Typography>
          </Box>
        </Box>
        {headerContent}
      </Box>
      )}

      <Box flex={1} minHeight={0} sx={{ overflowY: 'auto', px: flat ? 1.5 : 2.5, py: 2, background: '#fafbfd' }}>
        {messages.length === 0 && !loading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            textAlign="center"
            gap={1.5}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'rgba(0,136,207,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AutoAwesomeIcon sx={{ color: '#0088CF', fontSize: 28 }} />
            </Box>
            <Typography variant="h6">What would you like to know?</Typography>
            <Typography variant="body2" color="text.secondary" maxWidth={420}>
              Ask about your containers, sites, reuses, and environmental savings — answers come
              straight from your live data.
            </Typography>
            <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap" mt={0.5}>
              {SUGGESTIONS.map((suggestion) => (
                <Chip
                  key={suggestion}
                  label={suggestion}
                  variant="outlined"
                  onClick={() => sendMessage(suggestion)}
                  disabled={disabled}
                  sx={{ '&:hover': { borderColor: '#0088CF', color: '#0088CF' } }}
                />
              ))}
            </Box>
          </Box>
        ) : (
          messages.map((message, index) => <AiMessage key={index} message={message} />)
        )}
        {loading ? <AiWorkingBubble trace={trace} /> : null}
        {loading && partial ? (
          <AiMessage message={{ role: 'assistant', content: partial }} streaming />
        ) : null}
        {queuedMessages.map((queued, index) => (
          <Chip
            key={`${queued}-${index}`}
            label={`Queued: ${queued}`}
            onDelete={() => clearQueuedMessage(index)}
            size="small"
            sx={{ mr: 1, mt: 0.5 }}
          />
        ))}
        <div ref={bottomRef} />
      </Box>

      <Box px={1.5} pt={0.25} pb={0.75} sx={{ background: '#fff', borderTop: '1px solid #eef2f6' }}>
        <AiChatInput
          disabled={disabled}
          disabledReason={disabledReason}
          loading={loading}
          onSend={sendMessage}
          onStop={stopGeneration}
        />
        <Typography
          display="block"
          textAlign="center"
          mt={0.5}
          sx={{ fontSize: 10.5, color: '#9aa5b1', lineHeight: 1.3 }}
        >
          Conversations are saved and may be reviewed by VirtualStack staff — they are not private.
        </Typography>
      </Box>
    </Paper>
  );
};

export default AiChatView;
